import { AppStorage } from '../AppStorage';
import { IGpxSortDescriptor } from './GpxRouteApi';

export interface IGpxPanelState {
    search: string;
    tags: string[];
    /** The single user-chosen sort column (the server prepends tag order when tags are filtered). */
    sort: IGpxSortDescriptor;
    pageSize: number;
    page: number;
    /** Whether the search & filter section is expanded. */
    filtersOpen: boolean;
    /** Whether route notes are shown in the list at all. */
    showNotes: boolean;
    /** Whether route tags are shown in the list at all. */
    showTags: boolean;
}

const STORAGE_KEY = 'trailwise-gpx-panel';
const ALLOWED_PAGE_SIZES = [15, 30, 50];
const ALLOWED_SORT_FIELDS = ['name', 'created', 'updated', 'length'];

/**
 * The GPX panel's search/filter/sort/paging state, persisted to local storage
 * so it survives page reloads.
 */
export class GpxPanelState {

    private _storage = new AppStorage();
    private _state: IGpxPanelState;

    public constructor() {
        this._state = this.load();
    }

    get state(): IGpxPanelState {
        return this._state;
    }

    public update = (changes: Partial<IGpxPanelState>): IGpxPanelState => {
        this._state = { ...this._state, ...changes };
        this.save();
        return this._state;
    }

    private defaults(): IGpxPanelState {
        return {
            search: '',
            tags: [],
            sort: { field: 'name', descending: false },
            pageSize: 15,
            page: 1,
            filtersOpen: false,
            showNotes: true,
            showTags: true
        };
    }

    private load(): IGpxPanelState {
        const defaults = this.defaults();
        try {
            const raw = this._storage.getValue(STORAGE_KEY);
            if (!raw) {
                return defaults;
            }
            const parsed = JSON.parse(raw) as Partial<IGpxPanelState>;
            return {
                search: typeof parsed.search === 'string' ? parsed.search : defaults.search,
                //accept numbers too and normalise to string: a purely numeric tag can
                //have been persisted as a JSON number, and dropping it silently lost
                //the filter on reload
                tags: Array.isArray(parsed.tags)
                    ? parsed.tags
                        .filter(t => typeof t === 'string' || typeof t === 'number')
                        .map(t => String(t))
                    : defaults.tags,
                sort: this.readSort(parsed.sort, defaults.sort),
                pageSize: ALLOWED_PAGE_SIZES.indexOf(<number>parsed.pageSize) >= 0 ? <number>parsed.pageSize : defaults.pageSize,
                page: typeof parsed.page === 'number' && parsed.page >= 1 ? parsed.page : defaults.page,
                filtersOpen: typeof parsed.filtersOpen === 'boolean' ? parsed.filtersOpen : defaults.filtersOpen,
                showNotes: typeof parsed.showNotes === 'boolean' ? parsed.showNotes : defaults.showNotes,
                showTags: typeof parsed.showTags === 'boolean' ? parsed.showTags : defaults.showTags
            };
        } catch {
            return defaults;
        }
    }

    /**
     * Reads the persisted sort, tolerating the earlier two-level array form
     * (whose first entry becomes the single sort column).
     */
    private readSort(stored: unknown, fallback: IGpxSortDescriptor): IGpxSortDescriptor {
        const candidate = Array.isArray(stored)
            ? <IGpxSortDescriptor>stored[0]
            : <IGpxSortDescriptor>stored;
        if (!candidate || ALLOWED_SORT_FIELDS.indexOf(candidate.field) < 0) {
            return fallback;
        }
        return { field: candidate.field, descending: !!candidate.descending };
    }

    private save(): void {
        try {
            this._storage.setValue(STORAGE_KEY, JSON.stringify(this._state));
        } catch {
            //storage unavailable — state just won't persist
        }
    }
}
